import { useEffect, useRef, useState } from "react";
import Playlist from "./Playlist";
import { baseUrl } from "../../shared/baseUrl";
import "./Player.css";
import {
  ShuffleIcon,
  RepeatIcon,
  PlayPauseIcon,
  PrevIcon,
  NextIcon,
  QueueIcon,
  DragIcon,
  VolumeIcon,
  HeartFilledIcon,
  HeartOutlineIcon,
} from "../Icons";

function Player({
  queue: parentQueue,
  setQueue: setParentQueue,
  current: parentCurrent,
  setCurrent: setParentCurrent,
  playing: parentPlaying,
  setPlaying: setParentPlaying,
  liked: parentLiked,
  setLiked: setParentLiked,
  tempAudioId,
  settempAudioId,
}) {
  const audioRef = useRef(null);

  // ==========================
  // STATE
  // ==========================
  const [current, setCurrent] = useState(parentCurrent || 0);
  const [playing, setPlaying] = useState(parentPlaying || false);
  const [progress, setProgress] = useState(0);
  const [shuffle, setShuffle] = useState(
    () => JSON.parse(localStorage.getItem("shuffle")) || false,
  );
  const [repeat, setRepeat] = useState(
    () => JSON.parse(localStorage.getItem("repeat")) || false,
  );
  const [volume, setVolume] = useState(
    () => parseFloat(localStorage.getItem("volume")) || 0.7,
  );
  const [showQueue, setShowQueue] = useState(false);
  const [viewLiked, setViewLiked] = useState(false);

  const [queue, setQueue] = useState(parentQueue || []);
  const [liked, setLiked] = useState(parentLiked || []);

  const [duration, setDuration] = useState(0);

  // ==========================
  // SYNC WITH PARENT
  // ==========================
  useEffect(() => setQueue(parentQueue || []), [parentQueue]);
  useEffect(() => setLiked(parentLiked || []), [parentLiked]);
  useEffect(() => setCurrent(parentCurrent || 0), [parentCurrent]);
  useEffect(() => setPlaying(parentPlaying || false), [parentPlaying]);

  // ==========================
  // SAVE STATE
  // ==========================
  useEffect(
    () => localStorage.setItem("queueList", JSON.stringify(queue)),
    [queue],
  );
  useEffect(
    () => localStorage.setItem("likedAudioList", JSON.stringify(liked)),
    [liked],
  );
  useEffect(() => localStorage.setItem("volume", volume), [volume]);
  useEffect(
    () => localStorage.setItem("shuffle", JSON.stringify(shuffle)),
    [shuffle],
  );
  useEffect(
    () => localStorage.setItem("repeat", JSON.stringify(repeat)),
    [repeat],
  );

  useEffect(() => {
    localStorage.setItem("player_current", current);
    localStorage.setItem("player_playing", JSON.stringify(playing));
  }, [current, playing]);

  // ==========================
  // CURRENT AUDIO
  // ==========================
  const currentAudio =
    tempAudioId != null
      ? queue.find((s) => s.id === tempAudioId) || null
      : queue[current] || null;

  // ==========================
  // LOAD Audio (ONLY WHEN CHANGED)
  // ==========================
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentAudio) return;

    // Update audio source if changed
    if (audio.src !== currentAudio.src) {
      audio.src = currentAudio.src;
      audio.currentTime = 0;
      audio.volume = volume;

      if (playing) {
        audio.play().catch(() => {});
      }
    } else if (playing) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }

    // Set duration once audio is loaded
    audio.onloadedmetadata = () => setDuration(audio.duration);
  }, [currentAudio, playing, volume]);

  // ==========================
  // VOLUME (NO RESTART)
  // ==========================
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = volume;
  }, [volume]);

  const formatTime = (time = 0) => {
    if (!isFinite(time) || time < 0) return "00:00";

    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);

    const pad = (num) => String(num).padStart(2, "0");

    if (hours > 0) {
      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }

    return `${pad(minutes)}:${pad(seconds)}`;
  };

  // ==========================
  // PROGRESS
  // ==========================
  const updateProgress = () => {
    const audio = audioRef.current;
    if (!audio?.duration) return;
    setProgress((audio.currentTime / audio.duration) * 100);
  };

  const seek = (e) => {
    const bar = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - bar.left) / bar.width;
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    audio.currentTime = percent * audio.duration;
  };

  // ==========================
  // CONTROLS
  // ==========================
  const nextAudio = () => {
    if (!queue.length) return;
    const nextIndex = shuffle
      ? Math.floor(Math.random() * queue.length)
      : (current + 1) % queue.length;
    setCurrent(nextIndex);
    setParentCurrent(nextIndex);
  };

  const prevAudio = () => {
    if (!queue.length) return;
    const prevIndex = current === 0 ? queue.length - 1 : current - 1;
    setCurrent(prevIndex);
    setParentCurrent(prevIndex);
  };

  const toggleLike = () => {
    if (!currentAudio) return;

    let newLiked;
    if (liked.includes(currentAudio.id)) {
      newLiked = liked.filter((id) => id !== currentAudio.id);
    } else {
      newLiked = [...liked, currentAudio.id];
    }

    setLiked(newLiked);
    setParentLiked(newLiked);
  };

  const handlePlay = () => {
    const audio = audioRef.current;
    if (!audio || !currentAudio) return;

    if (!audio.playing) {
      audio.play().catch((err) => console.log("Autoplay blocked: ", err));
    }

    setPlaying(!playing);
  };

  // ==========================
  // RENDER
  // ==========================
  return (
    <>
      {showQueue && (
        <Playlist
          queue={queue}
          setQueue={setQueue}
          current={current}
          setCurrent={(i) => {
            setCurrent(i);
            setParentCurrent(i);
          }}
          setPlaying={setPlaying}
          liked={liked}
          close={() => setShowQueue(false)}
          viewLiked={viewLiked}
          setViewLiked={setViewLiked}
        />
      )}

      <div className="player">
        <audio
          ref={audioRef}
          onTimeUpdate={updateProgress}
          onEnded={() => (repeat ? audioRef.current.play() : nextAudio())}
        />

        {/* LEFT */}
        <div className="player-left">
          <img
            src={
              currentAudio?.cover || `${baseUrl}storage/images/quraan-icon.png`
            }
            alt={`${currentAudio?.title}`}
          />

          {currentAudio?.title && (
            <div>
              <div className="title">{currentAudio?.title}</div>
              <div className="riwaya">{currentAudio?.riwaya}</div>
              <div className="reciter">{currentAudio?.reciter}</div>
            </div>
          )}

          {currentAudio?.title && (
            <button
              className="like-btn"
              onClick={toggleLike}
              aria-label="Toggle Like"
            >
              {liked.includes(currentAudio?.id) ? (
                <HeartFilledIcon />
              ) : (
                <HeartOutlineIcon />
              )}
            </button>
          )}
        </div>

        {/* CENTER */}
        <div className="player-center">
          <div className="controls">
            <button
              className={shuffle ? "active" : ""}
              onClick={() => setShuffle(!shuffle)}
              aria-label="Toggle Shuffle"
            >
              <ShuffleIcon active={shuffle} />
            </button>

            <button onClick={prevAudio}>
              <PrevIcon />
            </button>

            <button className="play" onClick={handlePlay}>
              <PlayPauseIcon playing={playing} />
            </button>

            <button onClick={nextAudio}>
              <NextIcon />
            </button>

            <button
              className={repeat ? "active" : ""}
              onClick={() => setRepeat(!repeat)}
              aria-label="Toggle Repeat"
            >
              <RepeatIcon active={repeat} />
            </button>
          </div>

          {/* Time and Progress Bar */}
          <div className="time-container">
            <span className="current-time">
              {formatTime(audioRef.current?.currentTime || 0)}
            </span>

            <div className="progress-bar" onClick={seek}>
              <div className="progress" style={{ width: `${progress}%` }} />
            </div>

            <span className="duration">{formatTime(duration)}</span>
          </div>
        </div>

        {/* RIGHT */}
        <div className="player-right desktop-only">
          <button onClick={() => setVolume(volume === 0 ? 0.7 : 0)}>
            <VolumeIcon muted={volume === 0} />
          </button>

          <input
            className="volume-slider"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            style={{ "--fill": `${volume * 100}%` }}
          />

          <button onClick={() => setShowQueue(!showQueue)}>
            <QueueIcon />
          </button>
        </div>

        <div className="mobile-only">
          <button onClick={() => setShowQueue(!showQueue)}>
            <QueueIcon />
          </button>
        </div>
      </div>
    </>
  );
}

export default Player;
